import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface UseInfiniteScrollProps<T> {
  data: T[] | undefined;
  isLoading: boolean;
  totalPages: number | undefined;
  currentPage: number;
  onPageChange: (page: number) => void;
  queryKey: string[];
  resetTrigger?: string | undefined; // e.g., processingId - when it changes, reset pagination
}

export const useInfiniteScroll = <T>({
  data,
  isLoading,
  totalPages = 0,
  currentPage,
  onPageChange,
  queryKey,
  resetTrigger,
}: UseInfiniteScrollProps<T>) => {
  const queryClient = useQueryClient();
  const [allItems, setAllItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);
  const prevResetTriggerRef = useRef<string | undefined>(undefined);

  // Reset when resetTrigger changes (e.g., coming back from moderation)
  useEffect(() => {
    const wasOnOtherPage = prevResetTriggerRef.current !== undefined;
    const isNowOnListPage = !resetTrigger;

    if (wasOnOtherPage && isNowOnListPage) {
      queryClient.invalidateQueries({ queryKey });
      onPageChange(1);
      setHasMore(true);
    }

    prevResetTriggerRef.current = resetTrigger;
  }, [resetTrigger, queryKey, queryClient, onPageChange]);

  // Accumulate data when new page arrives
  useEffect(() => {
    if (!data) return;

    if (currentPage === 1) {
      setAllItems(data);
    } else {
      setAllItems((prev) => [...prev, ...data]);
    }

    setHasMore(currentPage < (totalPages || 0));
  }, [data, currentPage, totalPages]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!resetTrigger && observerTarget.current && allItems.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            hasMore &&
            !isLoading &&
            !resetTrigger
          ) {
            onPageChange(currentPage + 1);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '200px',
        }
      );

      observer.observe(observerTarget.current);

      return () => {
        observer.disconnect();
      };
    }
    return undefined;
  }, [
    hasMore,
    isLoading,
    allItems.length,
    currentPage,
    resetTrigger,
    onPageChange,
  ]);

  return {
    allItems,
    hasMore,
    observerTarget,
  };
};
